import React, { useEffect, useMemo } from 'react';
import {
  BundlesWrapper,
  IBundlesWrapperProps,
} from './components/Bundles/Wrapper';
import { BannerWrapper, IBannerWrapperProps } from './components/BannerWrapper';
import { MissingOffersBlock } from './components/MissingOffersBlock';
import {
  ISpecialOffersWrapperProps,
  SpecialOffersWrapper,
} from './components/SpecialOffers/Wrapper';
import {
  IRollingOffersWrapperProps,
  RollingOffersWrapper,
} from './components/RollingOffers/Wrapper';
import { StyledOffersWrapper } from './style';
import {
  EBundlesInternalViewModel,
  EQueryParams,
  StoreBodyBlock,
} from 'utils/enums';
import { IProductResponse, OfferData, StoreLayoutTheme } from 'utils/types';
import { useUrlQuery } from 'utils/index';
import { useStoreState } from 'state/hooks/store.state.hook';
import {
  IPlayerLevelWrapperProps,
  PlayerLevelWrapper,
} from './components/PlayerLevels/Wrapper';

export interface IShopPageProps {
  selectOffer: (offer: OfferData) => void;
  offersData?: any; // todo: send API.getOffers?.data
  storeTheme: StoreLayoutTheme;
  hasNoOffers: boolean;
  translations?: Record<string, string>;
  DailyBonusWrapper?: React.FC;
  tooltipOpenEventHandler?: () => void;
  tooltipLoadCallback?: (arr: string[]) => Promise<IProductResponse[]>;
  refetchOffers: () => void;
  sendEvent: (event: OfferData) => void;
  isRollingHorizontalViewEnabled?: boolean;
}

const DEFAULT_BODY_BLOCKS_ORDER: StoreBodyBlock[] = [
  StoreBodyBlock.dailyBonus,
  StoreBodyBlock.playerLevel,
  StoreBodyBlock.banners,
  StoreBodyBlock.specialOffers,
  StoreBodyBlock.rollingOffers,
  StoreBodyBlock.bundles,
];

const mapBlocksToComponents: Partial<Record<StoreBodyBlock, React.FC<any>>> = {
  [StoreBodyBlock.playerLevel]: PlayerLevelWrapper,
  [StoreBodyBlock.banners]: BannerWrapper,
  [StoreBodyBlock.specialOffers]: SpecialOffersWrapper,
  [StoreBodyBlock.rollingOffers]: RollingOffersWrapper,
  [StoreBodyBlock.bundles]: BundlesWrapper,
};

// I.E:  translations={{ subProductsPrefixText: t('bundles.subProductsPrefixText') }}

const DEFAULT_TRANSLATIONS = {
  subProductsPrefixText: 'Includes',
  availability: 'Available',
  collect: 'Collect',
  noOffersTitle: 'No Offers Available',
  noOffersMessage: 'No offers are available at the moment. Try again soon!',
};

export const StoreLayout: React.FC<IShopPageProps> = ({
  selectOffer,
  offersData,
  hasNoOffers,
  storeTheme: theme,
  translations,
  DailyBonusWrapper,
  tooltipOpenEventHandler,
  tooltipLoadCallback,
  refetchOffers,
  sendEvent,
  isRollingHorizontalViewEnabled = false,
}) => {
  const { setStoreTheme, setTranslations, storeTheme, setSendEvent } =
    useStoreState();
  const campaignOfferId = useUrlQuery(EQueryParams.OFFER_ID);

  useEffect(() => {
    if (!sendEvent) return;

    setSendEvent(sendEvent);
  }, [sendEvent]);

  useEffect(() => {
    setStoreTheme(theme);
  }, [theme]);

  useEffect(() => {
    setTranslations(translations ?? DEFAULT_TRANSLATIONS);
  }, [translations]);

  const bundlesInternalViewModel = useMemo(
    () => theme?.general?.bundlesInternalViewModel,
    [theme?.general?.bundlesInternalViewModel],
  );

  const bodyBlocksOrder = useMemo(() => {
    const themeBodyBlocks = theme?.storeTemplate?.bodyBlocksOrder;

    return themeBodyBlocks?.length
      ? themeBodyBlocks
      : DEFAULT_BODY_BLOCKS_ORDER;
  }, [theme?.storeTemplate?.bodyBlocksOrder]);

  const bannerProps: IBannerWrapperProps = useMemo(
    () => ({
      noOffers: Boolean(hasNoOffers || !offersData || campaignOfferId),
      bundlesInternalViewModel: bundlesInternalViewModel,
      bannerImage: offersData?.bannerImage || theme?.storeScreen?.defaultBanner,
      playerLevelImg: offersData?.playerLevelImg,
    }),
    [
      theme?.storeScreen?.defaultBanner,
      offersData,
      hasNoOffers,
      campaignOfferId,
      bundlesInternalViewModel,
    ],
  );

  const bundleWrapperProps: IBundlesWrapperProps = useMemo(
    () => ({
      selectOffer,
      sections: offersData?.sections,
      bundles: offersData?.offers?.bundles,
      bundleIdFocus: offersData?.offerFocus?.bundleId,
      tooltipOpenEventHandler,
      tooltipLoadCallback,
    }),
    [
      selectOffer,
      tooltipOpenEventHandler,
      tooltipLoadCallback,
      offersData?.sections,
      offersData?.offers?.bundles,
      offersData?.offerFocus?.bundleId,
    ],
  );

  const specialOffersWrapperProps: ISpecialOffersWrapperProps = useMemo(
    () => ({
      selectOffer,
      specialOffers: offersData?.offers?.promotions,
      tooltipOpenEventHandler,
      refetchOffers,
    }),
    [
      selectOffer,
      tooltipOpenEventHandler,
      refetchOffers,
      offersData?.offers?.promotions,
    ],
  );

  const playerLevels = useMemo(
    () =>
      offersData?.playerLevel
        ? [offersData?.playerLevel]
        : offersData?.playerLevels ?? [],
    [offersData?.playerLevel, offersData?.playerLevels],
  );

  const playerLevelWrapperProps: IPlayerLevelWrapperProps = useMemo(
    () => ({
      hasOffers: offersData?.offers,
      playerLevels,
      playerLevelImage: offersData?.playerLevelImg,
      refetchOffers,
    }),
    [
      refetchOffers,
      playerLevels,
      offersData?.offers,
      offersData?.playerLevelImg,
    ],
  );

  const rollingOffersWrapperProps: IRollingOffersWrapperProps = useMemo(
    () => ({
      rollingOffers: offersData?.offers?.rolling,
      refetchOffers,
      selectOffer,
      isHorizontalViewEnabled: isRollingHorizontalViewEnabled,
    }),
    [refetchOffers, selectOffer, offersData?.offers?.rolling],
  );

  const mapBlocksToProps: Partial<Record<StoreBodyBlock, Object>> = useMemo(
    () => ({
      [StoreBodyBlock.dailyBonus]: {},
      [StoreBodyBlock.playerLevel]: playerLevelWrapperProps,
      [StoreBodyBlock.banners]: bannerProps,
      [StoreBodyBlock.specialOffers]: specialOffersWrapperProps,
      [StoreBodyBlock.rollingOffers]: rollingOffersWrapperProps,
      [StoreBodyBlock.bundles]: bundleWrapperProps,
    }),
    [
      playerLevelWrapperProps,
      bannerProps,
      specialOffersWrapperProps,
      rollingOffersWrapperProps,
      bundleWrapperProps,
    ],
  );

  if (!storeTheme) return <></>;

  return (
    <StyledOffersWrapper className="store-layout" gap={1}>
      {bodyBlocksOrder.map((key: StoreBodyBlock) => {
        const Block: React.FC | undefined =
          key === StoreBodyBlock.dailyBonus
            ? DailyBonusWrapper
            : mapBlocksToComponents[key];
        if (Block) {
          return <Block key={key} {...mapBlocksToProps[key]} />;
        }
      })}

      <MissingOffersBlock hasNoOffers={hasNoOffers} />

      {(bundlesInternalViewModel === EBundlesInternalViewModel.BAGELCODE ||
        bundlesInternalViewModel === EBundlesInternalViewModel.BAGELCODE2) &&
        offersData?.playerLevelImg && (
          <div className="player-level-image">
            <img src={storeTheme!.general.logo} alt="logo" />
            <img alt="Player Level" src={offersData?.playerLevelImg} />
          </div>
        )}
    </StyledOffersWrapper>
  );
};
